/** Presentational pieces of the campaign console. No queries. */
import { Fragment } from "react";
import { clsx } from "clsx";

import {
  MEMBER_STATES,
  type ClosedAs,
  type ConsoleRule,
  type ConsoleThread,
  type DoNotContact,
  type GoalBar as GoalBarData,
  type MemberState,
  type PersonState,
  type Turn,
} from "../../api/console";
import {
  DUE_TONE,
  STATE_BAR,
  STATE_LABEL,
  TURN,
  berlinDate,
  closedLine,
  memberStateLabel,
  memberStateTone,
} from "../../lib/console";
import { Pill } from "../Pill";

/** One-line stacked bar for the overview table. */
export function FunnelBar({ funnel }: { funnel: Record<MemberState, number> }) {
  const total = MEMBER_STATES.reduce((n, s) => n + funnel[s], 0) || 1;
  return (
    <div className="min-w-[180px]">
      <div className="flex h-2 rounded-full overflow-hidden bg-surface-tint">
        {MEMBER_STATES.map((s) =>
          funnel[s] ? (
            <div
              key={s}
              className={STATE_BAR[s]}
              style={{ width: `${(funnel[s] / total) * 100}%` }}
              title={`${STATE_LABEL[s]} ${funnel[s]}`}
            />
          ) : null,
        )}
      </div>
      <div className="mt-1 flex gap-2 text-xs text-ink-low tnum">
        {MEMBER_STATES.map((s) => (
          <span key={s} title={STATE_LABEL[s]}>
            {funnel[s]}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The five fixed states as boxes, with how the closed ones closed. */
export function FunnelStrip({
  funnel,
  closed,
  parked,
  unit,
  yourTurn,
}: {
  funnel: Record<MemberState, number>;
  closed: Partial<Record<ClosedAs | "other", number>>;
  parked: number;
  unit: string;
  yourTurn: number;
}) {
  return (
    <div className="panel mb-4">
      <div className="text-xs text-ink-low mb-2">
        Funnel – counts {unit}; facts from the threads move it, nobody maintains it
        {parked ? ` · ${parked} parked` : ""}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {MEMBER_STATES.map((s, i) => (
          <div key={s} className="rounded border border-rule p-3">
            <div className="flex items-center gap-2 text-xs text-ink-low">
              <span className={clsx("inline-block w-2 h-2 rounded-full", STATE_BAR[s])} />
              {i > 0 ? "→ " : ""}
              {STATE_LABEL[s]}
            </div>
            <div className="text-2xl font-mono tnum text-ink mt-1">{funnel[s]}</div>
            <div className="text-xs text-ink-low mt-0.5">
              {s === "in_dialogue" && yourTurn ? `${yourTurn} your turn` : s === "closed" ? closedLine(closed) || "–" : " "}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TurnPill({ turn }: { turn: Turn }) {
  return <Pill value={TURN[turn].label} tone={TURN[turn].tone} />;
}

export function MemberStatePill({ state }: { state: PersonState }) {
  return <Pill value={memberStateLabel(state)} tone={memberStateTone(state)} />;
}

export function DueLine({ due }: { due: ConsoleThread["due"] }) {
  if (!due) return <span className="text-ink-low">–</span>;
  return (
    <div>
      <span className="font-mono tnum text-ink">{berlinDate(due.on)}</span>{" "}
      {due.source ? <Pill value={due.source} tone={DUE_TONE[due.source]} /> : null}
      {due.detail ? <div className="text-xs text-ink-low">{due.detail}</div> : null}
    </div>
  );
}

export function GoalBar({ bar }: { bar: GoalBarData }) {
  const pct = Math.max(0, Math.min(100, Math.round((bar.current / bar.target) * 100)));
  return (
    <div className="panel mb-4">
      <div className="flex items-baseline justify-between text-sm">
        <span>
          {bar.label}{" "}
          <span className="font-mono tnum text-ink">
            {bar.current} / {bar.target} {bar.unit ?? ""}
          </span>
        </span>
        {bar.note ? <span className="text-xs text-ink-low">{bar.note}</span> : null}
      </div>
      <div className="mt-2 h-2 rounded-full bg-surface-tint overflow-hidden">
        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function OpportunityStrip({ stages }: { stages: { id: string; name: string; count: number }[] }) {
  return (
    <div className="panel mb-4 text-sm">
      <span className="text-xs text-ink-low mr-2">After qualified – the opportunity pipeline carries the stage:</span>
      {stages.map((s, i) => (
        <span key={s.id} className="whitespace-nowrap">
          {i > 0 ? <span className="text-ink-low"> → </span> : null}
          {s.name} <span className="font-mono tnum text-ink">{s.count}</span>
        </span>
      ))}
    </div>
  );
}

export function RulesList({ rules }: { rules: ConsoleRule[] }) {
  if (!rules.length) {
    return (
      <div className="panel text-sm text-ink-low">
        No rules recorded. They live in the campaign's console config (campaign_upsert).
      </div>
    );
  }
  return (
    <div className="panel">
      <div className="text-xs text-ink-low mb-3">Read-only. The seat proposes rule changes; you approve them.</div>
      <dl className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
        {rules.map((r) => (
          <Fragment key={r.label}>
            <dt className="text-ink-low">{r.label}</dt>
            <dd className="text-ink">{r.value}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

/** The campaign's own "Do not contact" list (console config). */
export function DoNotContactTable({ rows }: { rows: DoNotContact[] }) {
  return (
    <div className="panel panel--flush">
      <div className="panel-header">
        <h2>Campaign list</h2>
        <span className="text-xs text-ink-low">from the campaign's console config</span>
      </div>
      {rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>Who</th>
              <th>Reason</th>
              <th>Since</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.who}>
                <td className="font-mono text-sm">{d.who}</td>
                <td className="text-sm">{d.reason}</td>
                <td className="font-mono text-xs text-ink-low">{berlinDate(d.since)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="px-6 py-4 text-sm text-ink-low">Nobody on this campaign's list.</div>
      )}
    </div>
  );
}
